// # Weaknesses Component for Step 1 Mentorship
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  FaChartBar, FaExclamationTriangle, FaSkull, FaBook,
  FaArrowRight, FaChartLine, FaChartPie
} from 'react-icons/fa';
import './stats1_weak.css';
import cacheManager from '../../../auth/cache_manager';
import PropTypes from 'prop-types';
import { getBasePath } from '../m1_access';

// # Import chapter icons
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import pathologyIcon from '../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import revisionIcon from '../../../general/library/step1/step1_icons/revision.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';

// # Chart colors
const CHART_COLORS = {
  primary: '#3498db',
  secondary: '#2ecc71',
  warning: '#f1c40f',
  danger: '#e74c3c',
  info: '#9b59b6'
};

// # Chapter icons mapping
const chapterIcons = {
  biochemistry: biochemistryIcon,
  immunology: immunologyIcon,
  microbiology: infectiousIcon,
  general_pathology: pathologyIcon,
  pharmacology: pharmaIcon,
  public_health_sciences: publicHealthIcon,
  anatomy_and_histology: anatomyIcon,
  cardiovascular: heartIcon,
  endocrine: endocrineIcon,
  gastrointestinal: gitIcon,
  hematology_and_oncology: bloodIcon,
  musculoskeletal_and_connective_tissue: musculoskeletalIcon,
  dermatology: skinIcon,
  neurology: cnsIcon,
  special_senses: eyeIcon,
  psychiatry: psychiatryIcon,
  renal: kidneyIcon,
  reproductive: reproductiveIcon,
  respiratory: lungsIcon,
  rapid_review: revisionIcon
};

function WeaknessesStats({ isAdmin, isTutor, isMentorView, isDarkMode, targetUserId }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('qbank');
  const [loading, setLoading] = useState(true);
  const [qbankData, setQbankData] = useState({
    uworld: {},
    amboss: {},
    nbme: {}
  });
  const [weakSpots, setWeakSpots] = useState([]);
  const [libraryHardTopics, setLibraryHardTopics] = useState([]);
  const [libraryOhNoTopics, setLibraryOhNoTopics] = useState([]);
  const [stats, setStats] = useState({
    totalWeakPoints: 0,
    qbankDistribution: [],
    weakSpotsByChapter: [],
    hardTopicsByChapter: [],
    ohNoTopicsByChapter: []
  });

  // # Load QBank data
  useEffect(() => {
    // # Get the correct user ID based on roles
    const userId = targetUserId || user?.uid;
    if (!userId) return;

    const docRef = doc(db, 's1_mentee', userId);
    const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setQbankData({
          uworld: data.uworld || {},
          amboss: data.amboss || {},
          nbme: data.nbme || {}
        });
      }
    });

    return () => unsubscribe();
  }, [user, targetUserId]);

  // # Load weak spots data
  useEffect(() => {
    // # Get the correct user ID based on roles
    const userId = targetUserId || user?.uid;
    if (!userId) return;

    const docRef = doc(db, 's1_mentee', userId);
    const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.weakSpots) {
          setWeakSpots(Object.values(data.weakSpots));
        }
      }
    });

    return () => unsubscribe();
  }, [user, targetUserId]);

  // # Load library topics data
  useEffect(() => {
    // # Get the correct user ID based on roles
    const userId = targetUserId || user?.uid;
    if (!userId) return;

    const docRef = doc(db, 'my_lib_progress', userId);
    const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data?.step1) {
          const hardTopics = [];
          const ohNoTopics = [];

          // # Process each chapter
          Object.entries(data.step1).forEach(([chapter, chapterData]) => {
            // # Skip if chapterData is null or not an object
            if (!chapterData || typeof chapterData !== 'object') return;

            // # Process each topic in the chapter
            Object.entries(chapterData).forEach(([topicId, topicData]) => {
              // # Skip metadata fields and null/undefined topics
              if (topicId === 'iscomplete' || 
                  topicId === 'iscomplete_at' || 
                  topicId === 'IsChapStarted' || 
                  topicId === 'ChptStartedAt' || 
                  !topicData) {
                return;
              }

              // # Only process topics with valid difficulty
              if (topicData.completed && topicData.difficulty) {
                const topicInfo = {
                  chapter,
                  topicId,
                  name: topicId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                  completedAt: topicData.completedAt || null,
                  reviewTimestamps: topicData.reviewTimestamps || [],
                  difficulty: topicData.difficulty
                };

                if (topicData.difficulty === 'hard') {
                  hardTopics.push(topicInfo);
                } else if (topicData.difficulty === 'oh_no') {
                  ohNoTopics.push(topicInfo);
                }
              }
            });
          });

          // # Sort topics by completion date (newest first)
          const sortByDate = (a, b) => {
            if (!a.completedAt) return 1;
            if (!b.completedAt) return -1;
            return new Date(b.completedAt) - new Date(a.completedAt);
          };

          setLibraryHardTopics(hardTopics.sort(sortByDate));
          setLibraryOhNoTopics(ohNoTopics.sort(sortByDate));
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, targetUserId]);

  // # Calculate statistics
  useEffect(() => {
    // # Calculate QBank distribution
    const qbankWeakPoints = {
      uworld: Object.values(qbankData.uworld || {}).reduce((acc, topic) => 
        acc + ((topic?.weaknesses?.length || 0)), 0),
      amboss: Object.values(qbankData.amboss || {}).reduce((acc, topic) => 
        acc + ((topic?.weaknesses?.length || 0)), 0),
      nbme: Object.values(qbankData.nbme || {}).reduce((acc, topic) => 
        acc + ((topic?.weaknesses?.length || 0)), 0)
    };

    // # Calculate weak spots by chapter
    const weakSpotsByChapter = weakSpots.reduce((acc, spot) => {
      if (spot?.chapter) {
        acc[spot.chapter] = (acc[spot.chapter] || 0) + 1;
      }
      return acc;
    }, {});

    // # Calculate hard topics by chapter
    const hardTopicsByChapter = libraryHardTopics.reduce((acc, topic) => {
      if (topic?.chapter) {
        acc[topic.chapter] = (acc[topic.chapter] || 0) + 1;
      }
      return acc;
    }, {});

    // # Calculate oh no topics by chapter
    const ohNoTopicsByChapter = libraryOhNoTopics.reduce((acc, topic) => {
      if (topic?.chapter) {
        acc[topic.chapter] = (acc[topic.chapter] || 0) + 1;
      }
      return acc;
    }, {});

    setStats({
      totalWeakPoints: Object.values(qbankWeakPoints).reduce((a, b) => a + b, 0) + 
                      weakSpots.length + 
                      libraryHardTopics.length + 
                      libraryOhNoTopics.length,
      qbankDistribution: [
        { name: 'UWorld', value: qbankWeakPoints.uworld, color: CHART_COLORS.primary },
        { name: 'AMBOSS', value: qbankWeakPoints.amboss, color: CHART_COLORS.secondary },
        { name: 'NBME', value: qbankWeakPoints.nbme, color: CHART_COLORS.warning }
      ],
      weakSpotsByChapter: Object.entries(weakSpotsByChapter).map(([chapter, count]) => ({
        name: chapter,
        value: count,
        color: CHART_COLORS.info
      })),
      hardTopicsByChapter: Object.entries(hardTopicsByChapter).map(([chapter, count]) => ({
        name: chapter,
        value: count,
        color: CHART_COLORS.warning
      })),
      ohNoTopicsByChapter: Object.entries(ohNoTopicsByChapter).map(([chapter, count]) => ({
        name: chapter,
        value: count,
        color: CHART_COLORS.danger
      }))
    });
  }, [qbankData, weakSpots, libraryHardTopics, libraryOhNoTopics]);

  // # Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // # Handle topic navigation
  const handleTopicClick = (chapter, topicId) => {
    const isAdmin = window.location.pathname.startsWith('/admin');
    let basePath;
    
    if (isAdmin) {
      basePath = '/admin/library/step1';
    } else if (isTutor) {
      basePath = '/tutor-dashboard/library/step1';
    } else {
      basePath = '/library/step1';
    }
    
    navigate(`${basePath}/${chapter}/topic/${topicId}`);
  };

  // # Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="stats1_weak_tooltip">
          <p className="stats1_weak_tooltip_label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="stats1_weak_tooltip_value" style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`stats1_weak_container ${isDarkMode ? 'dark' : ''}`}>
      {/* Summary Cards */}
      <div className="stats1_weak_summary">
        <div className="stats1_weak_card">
          <h3>Total Weak Points</h3>
          <div className="stats1_weak_value">{stats.totalWeakPoints}</div>
          <div className="stats1_weak_breakdown">
            <div>
              <span>QBank</span>
              <span>{stats.qbankDistribution.reduce((acc, item) => acc + item.value, 0)}</span>
            </div>
            <div>
              <span>Weak Spots</span>
              <span>{weakSpots.length}</span>
            </div>
            <div>
              <span>Hard Topics</span>
              <span>{libraryHardTopics.length}</span>
            </div>
            <div>
              <span>Very Hard Topics</span>
              <span>{libraryOhNoTopics.length}</span>
            </div>
          </div>
        </div>

        <div className="stats1_weak_card">
          <h3>Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={stats.qbankDistribution}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, value }) => `${name}: ${value}`}
              >
                {stats.qbankDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="stats1_weak_tabs">
        <button 
          className={`stats1_weak_tab ${activeTab === 'qbank' ? 'active' : ''}`}
          onClick={() => handleTabChange('qbank')}
        >
          <FaChartBar /> QBank Weak Points
        </button>
        <button 
          className={`stats1_weak_tab ${activeTab === 'weakspots' ? 'active' : ''}`}
          onClick={() => handleTabChange('weakspots')}
        >
          <FaExclamationTriangle /> My Weak Spots
        </button>
        <button 
          className={`stats1_weak_tab ${activeTab === 'hard' ? 'active' : ''}`}
          onClick={() => handleTabChange('hard')}
        >
          <FaBook /> Hard Topics
        </button>
        <button 
          className={`stats1_weak_tab ${activeTab === 'ohno' ? 'active' : ''}`}
          onClick={() => handleTabChange('ohno')}
        >
          <FaSkull /> Very Hard Topics
        </button>
      </div>

      {/* Tab Content */}
      <div className="stats1_weak_content">
        {activeTab === 'qbank' && (
          <div className="stats1_weak_qbank">
            <h2>QBank Weak Points</h2>
            {/* UWorld Weaknesses */}
            <div className="stats1_weak_qbank_section">
              <h3>UWorld</h3>
              <div className="stats1_weak_topics_grid">
                {Object.entries(qbankData.uworld).map(([system, data]) => (
                  data.weaknesses && data.weaknesses.length > 0 && (
                    <div key={system} className="stats1_weak_topic_card">
                      <div className="stats1_weak_topic_header">
                        <h4>{system}</h4>
                        <span className="stats1_weak_count">{data.weaknesses.length}</span>
                      </div>
                      <ul className="stats1_weak_list">
                        {data.weaknesses.map((weakness, index) => (
                          <li key={index}>
                            <strong>{index + 1}.</strong> {weakness.trim()}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            </div>

            {/* AMBOSS Weaknesses */}
            <div className="stats1_weak_qbank_section">
              <h3>AMBOSS</h3>
              <div className="stats1_weak_topics_grid">
                {Object.entries(qbankData.amboss).map(([system, data]) => (
                  data.weaknesses && data.weaknesses.length > 0 && (
                    <div key={system} className="stats1_weak_topic_card">
                      <div className="stats1_weak_topic_header">
                        <h4>{system}</h4>
                        <span className="stats1_weak_count">{data.weaknesses.length}</span>
                      </div>
                      <ul className="stats1_weak_list">
                        {data.weaknesses.map((weakness, index) => (
                          <li key={index}>
                            <strong>{index + 1}.</strong> {weakness.trim()}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            </div>

            {/* NBME Weaknesses */}
            <div className="stats1_weak_qbank_section">
              <h3>NBME</h3>
              <div className="stats1_weak_topics_grid">
                {Object.entries(qbankData.nbme).map(([exam, data]) => (
                  data.weaknesses && data.weaknesses.length > 0 && (
                    <div key={exam} className="stats1_weak_topic_card">
                      <div className="stats1_weak_topic_header">
                        <h4>{exam}</h4>
                        <span className="stats1_weak_count">{data.weaknesses.length}</span>
                      </div>
                      <ul className="stats1_weak_list">
                        {data.weaknesses.map((weakness, index) => (
                          <li key={index}>
                            <strong>{index + 1}.</strong> {weakness.trim()}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'weakspots' && (
          <div className="stats1_weak_spots">
            <h2>My Weak Spots</h2>
            <div className="stats1_weak_spots_chart">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.weakSpotsByChapter}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="value" fill={CHART_COLORS.info} name="Weak Spots" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="stats1_weak_spots_grid">
              {weakSpots.map((spot, index) => (
                <div key={index} className="stats1_weak_spot_card">
                  <div className="stats1_weak_spot_header">
                    <img 
                      src={chapterIcons[spot.chapter.toLowerCase().replace(/\s+/g, '_')]} 
                      alt={spot.chapter} 
                      className="stats1_weak_spot_icon"
                    />
                    <div className="stats1_weak_spot_info">
                      <h4>{spot.chapter}</h4>
                      <p>{spot.topic}</p>
                    </div>
                  </div>
                  <div className="stats1_weak_spot_details">
                    <p>{spot.details}</p>
                    <div className="stats1_weak_spot_meta">
                      <span>Reviews: {spot.reviewCount || 0}</span>
                      <span>Created: {new Date(spot.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'hard' && (
          <div className="stats1_weak_hard">
            <h2>Hard Topics</h2>
            <div className="stats1_weak_hard_chart">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.hardTopicsByChapter}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="value" fill={CHART_COLORS.warning} name="Hard Topics" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="stats1_weak_topics_grid">
              {libraryHardTopics.map((topic, index) => (
                <div 
                  key={index} 
                  className="stats1_weak_topic_card clickable"
                  onClick={() => handleTopicClick(topic.chapter, topic.topicId)}
                >
                  <div className="stats1_weak_topic_header">
                    <img 
                      src={chapterIcons[topic.chapter]} 
                      alt={topic.chapter} 
                      className="stats1_weak_topic_icon"
                    />
                    <div className="stats1_weak_topic_info">
                      <h4>{topic.chapter.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</h4>
                      <p>{topic.topicId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</p>
                    </div>
                    <FaArrowRight className="stats1_weak_topic_arrow" />
                  </div>
                  <div className="stats1_weak_topic_meta">
                    <span>Completed: {new Date(topic.completedAt).toLocaleDateString()}</span>
                    <span>Reviews: {topic.reviewTimestamps?.length || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'ohno' && (
          <div className="stats1_weak_ohno">
            <h2>Very Hard Topics</h2>
            <div className="stats1_weak_ohno_chart">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.ohNoTopicsByChapter}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="value" fill={CHART_COLORS.danger} name="Very Hard Topics" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="stats1_weak_topics_grid">
              {libraryOhNoTopics.map((topic, index) => (
                <div 
                  key={index} 
                  className="stats1_weak_topic_card clickable danger"
                  onClick={() => handleTopicClick(topic.chapter, topic.topicId)}
                >
                  <div className="stats1_weak_topic_header">
                    <img 
                      src={chapterIcons[topic.chapter]} 
                      alt={topic.chapter} 
                      className="stats1_weak_topic_icon"
                    />
                    <div className="stats1_weak_topic_info">
                      <h4>{topic.chapter.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</h4>
                      <p>{topic.topicId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</p>
                    </div>
                    <FaArrowRight className="stats1_weak_topic_arrow" />
                  </div>
                  <div className="stats1_weak_topic_meta">
                    <span>Completed: {new Date(topic.completedAt).toLocaleDateString()}</span>
                    <span>Reviews: {topic.reviewTimestamps?.length || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// # Update PropTypes
WeaknessesStats.propTypes = {
  isDarkMode: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isTutor: PropTypes.bool,
  isMentorView: PropTypes.bool,
  targetUserId: PropTypes.string
};

export default WeaknessesStats;
